import React, { useCallback, useState } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Divider, Typography } from '@mui/material';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import CartItem from './CartItem';
import CmtCardFooter from '@coremat/CmtCard/CmtCardFooter';
import {
  useActiveCart,
  useDeleteCart,
  useDeleteCartItem,
  useDeleteCartTooling,
  useDeleteCartWall,
  usePatchApplyPromoCode,
} from 'services/Data/CartService';
import DeleteModal from '@acnb/CustomComponents/_Modals/DeleteModal';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import useMobile from 'services/Hooks/useMobile';
import { useHistory } from 'react-router-dom';
import CartWall from './CartWall';
import CartTooling from './CartTooling';
import { useSelector } from 'react-redux';
import StorePageHeader from 'Components/StorePageHeader';
import { IsImpersonationMode } from 'config/config';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cartFooter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const CompleteOrder = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const accountReducer = useSelector(state => state.AccountReducer);
  const dealerId = accountReducer?.profile?.dealer_Id;

  const { data: cart, isLoading: gettingCart } = useActiveCart(
    accountReducer.isLoggedIn,
    true,
    IsImpersonationMode ? dealerId : null,
  );
  const [itemToDelete, setItemToDelete] = useState();
  const [wallToDelete, setWallToDelete] = useState();
  const [toolingToDelete, setToolingToDelete] = useState();
  const [cartToEmpty, setCartToEmpty] = useState();
  const { mutate: deleteCart } = useDeleteCart();
  const { mutate: applyPromoCode } = usePatchApplyPromoCode();
  const { mutate: deleteItem } = useDeleteCartItem();
  const { mutate: deleteWall } = useDeleteCartWall();
  const { mutate: deleteTooling } = useDeleteCartTooling();

  const isMobile = useMobile();

  const handleOpenDeleteItemModal = useCallback(
    item => {
      setItemToDelete(item);
    },
    [setItemToDelete],
  );

  const handleOpenDeleteWallModal = useCallback(
    wall => {
      setWallToDelete(wall);
    },
    [setWallToDelete],
  );

  const handleOpenDeleteToolingModal = useCallback(
    tooling => {
      setToolingToDelete(tooling);
    },
    [setToolingToDelete],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setItemToDelete(null);
    setWallToDelete(null);
    setToolingToDelete(null);
  }, [setItemToDelete, setWallToDelete, setToolingToDelete]);

  const handleOpenEmptyCart = useCallback(
    cart => {
      setCartToEmpty(cart);
    },
    [setCartToEmpty],
  );

  const handleCloseEmptyCart = useCallback(() => {
    setCartToEmpty(null);
  }, [setCartToEmpty]);

  const totalSavings = cart?.cartItems?.reduce((acc, item) => {
    if (item.unitPricePromotion > 0) {
      return acc + (item.unitPrice - item.unitPricePromotion) * item.quantity;
    }
    return acc;
  }, 0);

  return (
    <>
      <Box sx={{ mb: 9 }}>
        <StorePageHeader title={t('cart')} imgLeft backgroundColor="lightGray" borderRadius="0.5rem" />
      </Box>

      <Box className={classes.pageFull}>
        <DeleteModal
          handleCloseModal={handleCloseDeleteModal}
          item={itemToDelete}
          message={t('deleteItemFromCartText', {
            sku: itemToDelete?.sku,
            name: itemToDelete?.name,
          })}
          deleteAction={deleteItem}
        />
        <DeleteModal
          handleCloseModal={handleCloseDeleteModal}
          item={wallToDelete}
          message={t('deleteWallFromCartText', {
            name: wallToDelete ? `${wallToDelete.name}` : '',
          })}
          deleteAction={deleteWall}
        />

        <DeleteModal
          handleCloseModal={handleCloseDeleteModal}
          item={toolingToDelete}
          message={t('deleteToolingFromCartText', {
            name: toolingToDelete ? toolingToDelete.name : '',
          })}
          deleteAction={deleteTooling}
        />

        <DeleteModal
          handleCloseModal={handleCloseEmptyCart}
          item={cartToEmpty}
          message={t('deleteCartWarning')}
          deleteAction={deleteCart}
          deleteLabel={t('empty')}
        />

        <CmtCard>
          {/* <CmtCardHeader title={t('order')} icon={<ShoppingCart />}></CmtCardHeader> */}
          <CmtCardContent>
            {/* Section for the Cart wall */}
            {cart &&
              cart?.cartWalls?.map((wall, i) => (
                <CartWall index={i} wall={wall} handleOpenDeleteWallModal={handleOpenDeleteWallModal} />
              ))}
            {/* Section for the Cart tooling */}
            {cart &&
              cart?.cartToolings
                ?.sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated))
                .map((tooling, i) => (
                  <CartTooling
                    index={i + cart?.cartWalls?.length}
                    key={tooling.id}
                    tooling={tooling}
                    handleOpenDeleteToolingModal={handleOpenDeleteToolingModal}
                  />
                ))}
            {/* Section for the Cart items */}
            {cart &&
              cart?.cartItems?.map((item, i) => (
                <React.Fragment key={item.id}>
                  {i !== 0 && cart?.cartWalls?.length === 0 && cart?.cartToolings?.length === 0 && (
                    <Divider sx={{ mb: 4 }} />
                  )}
                  <Divider sx={{ mb: 4 }} />

                  <CartItem index={i} item={item} handleOpenDeleteItemModal={handleOpenDeleteItemModal} />

                  {i + 1 === cart?.cartItems?.length && <Divider sx={{ mb: 4 }} />}
                </React.Fragment>
              ))}

            {!cart && (
              <Box className={classes.noItem}>
                <Typography fontSize={20} color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                  {t('NoItemInCart')}
                </Typography>
              </Box>
            )}
          </CmtCardContent>
          <CmtCardFooter>
            <GridContainer className={isMobile ? '' : classes.cartFooter}>
              <GridItem xs={12} md={7} lg={9}>
                <Box>
                  <Button
                    variant="contained"
                    sx={{ borderRadius: 0 }}
                    color="error"
                    onClick={() => handleOpenEmptyCart(cart)}
                    disabled={!cart}>
                    <Typography variant="h5">{t('wipeCart')}</Typography>
                  </Button>
                </Box>
              </GridItem>
              <GridItem xs={12} md={5} lg={3}>
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 33,
                  }}
                  isLoading={gettingCart}
                  component={
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <Box>
                          <Typography variant="h1">{t('subTotal')}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h1">${(cart?.subTotal ?? 0).toFixed(2)}</Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        {totalSavings > 0 && (
                          <>
                            <Box>
                              <Typography color="error" variant="h2">
                                {t('totalSavings')}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography color="error" variant="h2">
                                ${totalSavings.toFixed(2)}
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                      <Box sx={{ my: 2 }}>
                        <CustomInput
                          labelText={t('promoCode')}
                          id="city"
                          formControlProps={{
                            fullWidth: true,
                          }}
                          inputProps={{
                            onBlur: ({ target: { value } }) => {
                              if (cart?.promoCode !== value) {
                                applyPromoCode({ ...cart, promoCode: value });
                              }
                            },
                            disabled: !cart,
                            defaultValue: cart?.promoCode ?? '',
                          }}
                        />

                        {cart?.promoCode && cart?.promoCodeDiscount > 0 && (
                          <Alert severity="success">{t('valid_promo_code')}</Alert>
                        )}
                        {cart?.promoCode && cart?.promoCodeDiscount === null && (
                          <Alert severity="error">{t('invalid_promo_code')}</Alert>
                        )}
                      </Box>

                      {cart?.promoCodeDiscount && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}>
                          <Box>
                            <Typography variant="h2">{t('discount')}</Typography>
                          </Box>
                          <Box>
                            <Typography x variant="h2">
                              ${(cart?.promoCodeDiscount ?? 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {cart?.subTotal - cart?.promoCodeDiscount < 0 && (
                        <Alert severity="warning">
                          {t('cartDiscountWarning', {
                            discountLeftOver: `$${(cart?.promoCodeDiscount - cart?.subTotal).toFixed(2)}`,
                          })}
                        </Alert>
                      )}
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography fontSize={15}>{t('shippingFeeNotIncluded')}</Typography>
                      </Alert>
                      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          color="secondary"
                          sx={{
                            borderRadius: 0,
                            color: '#000',
                            mt: 6,
                            width: '100%',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: '#EFBA29',
                              boxShadow: 'none',
                            },
                          }}
                          disabled={!cart}
                          onClick={() => history.push('/dealer/cart/checkout')}>
                          <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 }, py: 2 }}>
                            {t('proceedCheckout')}
                          </Typography>
                        </Button>
                      </Box>
                    </Box>
                  }
                />
              </GridItem>
            </GridContainer>
          </CmtCardFooter>
        </CmtCard>
      </Box>
    </>
  );
};

export default CompleteOrder;
